import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Upload, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useCart, k } from '../../context/CartContext';
import { WelcomeCardFields } from '../../components/shop/SetExtras';
import { CartLines } from '../../components/shop/CartDrawer';
import { SHOP_API, BACKEND, DIVISIONS, PAY_LOGOS, taka } from '../../components/shop/shopUtils';
import { useShopSettings } from './Shop';

const SUBURBS = ['Gazipur', 'Narayanganj', 'Munshiganj', 'Manikganj', 'Narsingdi'];
const field = 'w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#00E676]';
const L = ({ label, children }) => <label className="block text-sm"><span className="mb-1 block font-medium">{label}</span>{children}</label>;

export const PayLogos = () => (
  <div className="flex flex-wrap gap-1.5" data-testid="payment-logos">{PAY_LOGOS.map((m) => <span key={m} className="rounded border border-[#E5E7EB] bg-white px-2 py-0.5 text-[11px] font-bold text-[#6B7280]">{m}</span>)}</div>
);

export function CartPage() {
  const { t, language } = useLanguage();
  const cart = useCart();
  const navigate = useNavigate();
  return (
    <div className="shop-container py-8" data-testid="cart-page">
      <h1 className="shop-heading mb-4 text-3xl">{t({ en: 'Your cart', bn: 'আপনার কার্ট' })}</h1>
      <CartLines />
      {cart.items.length > 0 && (
        <div className="mt-6 flex flex-col items-end gap-3 border-t border-[#E5E7EB] pt-4">
          <p className="text-lg font-semibold">{t({ en: 'Subtotal', bn: 'সাবটোটাল' })}: {taka(cart.subtotal, language)}</p>
          <button onClick={() => navigate('/checkout')} className="shop-btn w-full bg-[#E31E24] py-3 text-white sm:w-auto" data-testid="cart-page-checkout">{t({ en: 'Checkout', bn: 'চেকআউট' })}</button>
        </div>
      )}
    </div>
  );
}

export default function Checkout() {
  const { t, language } = useLanguage();
  const cart = useCart();
  const navigate = useNavigate();
  const s = useShopSettings();
  const [f, setF] = useState({ name: '', mobile: '', email: '', division: '', district: '', upazila: '', address: '', note: '' });
  const [pay, setPay] = useState('cod');
  const [txn, setTxn] = useState('');
  const [shot, setShot] = useState(null);
  const [coupon, setCoupon] = useState('');
  const [disc, setDisc] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState({});
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, ...(k === 'division' ? { district: '' } : {}) });

  const zone = useMemo(() => {
    if (!s || !f.district) return null;
    const id = f.district === 'Dhaka' ? 'dhaka' : SUBURBS.includes(f.district) ? 'suburbs' : 'outside';
    return s.zones.find((z) => z.id === id);
  }, [s, f.district]);
  const delivery = !zone ? 0 : zone.id === 'dhaka' && cart.subtotal >= s.free_threshold ? 0 : zone.charge;
  const total = cart.subtotal - (disc?.discount || 0) + delivery;
  const codOk = s?.cod_enabled && total <= s?.cod_limit;
  useEffect(() => { if (s && !codOk && pay === 'cod') setPay('bkash'); }, [s, codOk, pay]);
  const items = cart.items.map((i) => ({ slug: i.slug, variant_sku: i.variant_sku, qty: i.qty, personalisation: i.personalisation || null, gift_message: i.gift_message || null,
    ...(i.baby && cards[k(i)] ? { baby_name: cards[k(i)].baby_name || null, baby_dob: cards[k(i)].baby_dob || null } : {}) }));
  const babyLines = cart.items.filter((i) => i.baby);

  const applyCoupon = async () => {
    setErr('');
    const r = await fetch(`${SHOP_API}/coupon/validate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: coupon, items }) });
    const d = await r.json();
    if (r.ok) setDisc(d); else { setDisc(null); setErr(d.detail); }
  };

  const upload = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`${BACKEND}/api/membership/upload`, { method: 'POST', body: fd });
    if (r.ok) setShot((await r.json()).url);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!/^(?:\+?880|0)1[3-9]\d{8}$/.test(f.mobile.replace(/\s/g, ''))) return setErr(t({ en: 'Enter a valid Bangladeshi mobile number (01XXXXXXXXX).', bn: 'সঠিক মোবাইল নম্বর দিন (01XXXXXXXXX)।' }));
    setBusy(true);
    const r = await fetch(`${SHOP_API}/orders`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customer: { ...f, email: f.email || null }, items, coupon: disc?.code, payment_method: pay, payment_txn: txn || null, payment_screenshot_url: shot, lang: language }) });
    const d = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) return setErr(typeof d.detail === 'string' ? d.detail : t({ en: 'Please check the form.', bn: 'ফর্মটি যাচাই করুন।' }));
    cart.clear();
    navigate(`/order/${d.order_number}?mobile=${d.mobile}&new=1`);
  };

  if (cart.items.length === 0) return <div className="shop-container py-20 text-center" data-testid="checkout-empty"><p className="text-[#6B7280]">{t({ en: 'Your cart is empty.', bn: 'আপনার কার্ট খালি।' })}</p><button onClick={() => navigate('/shop')} className="shop-btn mt-4 bg-[#0E9A49] text-white">{t({ en: 'Go to shop', bn: 'শপে যান' })}</button></div>;
  if (s?.paused) return <div className="shop-container py-20 text-center text-[#E31E24]" data-testid="checkout-paused">{t(s.pause_message)}</div>;

  const payOpt = (id, label, disabled) => (
    <label className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2.5 text-sm ${pay === id ? 'border-[#E31E24] bg-[#FEE2E2]' : 'border-[#E5E7EB]'} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`} data-testid={`pay-option-${id}`}>
      <input type="radio" name="pay" checked={pay === id} disabled={disabled} onChange={() => setPay(id)} /> {label}
    </label>
  );

  return (
    <form onSubmit={submit} className="shop-container grid gap-8 py-8 lg:grid-cols-[1fr_380px]" data-testid="checkout-page">
      <div className="space-y-6">
        <h1 className="shop-heading text-3xl">{t({ en: 'Checkout', bn: 'চেকআউট' })}</h1>
        <section className="grid gap-4 sm:grid-cols-2">
          <L label={t({ en: 'Full name *', bn: 'পূর্ণ নাম *' })}><input required className={field} value={f.name} onChange={set('name')} data-testid="co-name" /></L>
          <L label={t({ en: 'Mobile (+880) *', bn: 'মোবাইল (+৮৮০) *' })}><input required inputMode="tel" placeholder="01XXXXXXXXX" className={field} value={f.mobile} onChange={set('mobile')} data-testid="co-mobile" /></L>
          <L label={t({ en: 'Email (for confirmation)', bn: 'ইমেইল (নিশ্চিতকরণের জন্য)' })}><input type="email" className={field} value={f.email} onChange={set('email')} data-testid="co-email" /></L>
          <L label={t({ en: 'Division *', bn: 'বিভাগ *' })}><select required className={field} value={f.division} onChange={set('division')} data-testid="co-division"><option value="">—</option>{Object.keys(DIVISIONS).map((d) => <option key={d}>{d}</option>)}</select></L>
          <L label={t({ en: 'District *', bn: 'জেলা *' })}><select required className={field} value={f.district} onChange={set('district')} disabled={!f.division} data-testid="co-district"><option value="">—</option>{(DIVISIONS[f.division] || []).map((d) => <option key={d}>{d}</option>)}</select></L>
          <L label={t({ en: 'Upazila / Thana *', bn: 'উপজেলা / থানা *' })}><input required className={field} value={f.upazila} onChange={set('upazila')} data-testid="co-upazila" /></L>
          <div className="sm:col-span-2"><L label={t({ en: 'Address *', bn: 'ঠিকানা *' })}><textarea required rows={2} className={field} value={f.address} onChange={set('address')} data-testid="co-address" /></L></div>
          <div className="sm:col-span-2"><L label={t({ en: 'Order note', bn: 'অর্ডার নোট' })}><input className={field} value={f.note} onChange={set('note')} data-testid="co-note" /></L></div>
        </section>
        {babyLines.length > 0 && <section className="space-y-3" data-testid="welcome-cards">{babyLines.map((i) => <WelcomeCardFields key={k(i)} line={i} value={cards[k(i)] || {}} onChange={(v) => setCards({ ...cards, [k(i)]: v })} />)}</section>}
        <section>
          <h2 className="shop-heading mb-3 text-xl">{t({ en: 'Payment', bn: 'পেমেন্ট' })}</h2>
          <div className="grid gap-2 sm:grid-cols-3">
            {payOpt('cod', t({ en: 'Cash on delivery', bn: 'ক্যাশ অন ডেলিভারি' }), !codOk)}
            {payOpt('bkash', 'bKash')}
            {payOpt('nagad', t({ en: 'Nagad', bn: 'নগদ' }))}
          </div>
          {!codOk && s && <p className="mt-2 text-xs text-[#6B7280]" data-testid="cod-unavailable">{t({ en: `Cash on delivery is available for orders up to ${taka(s.cod_limit, 'en')}.`, bn: `ক্যাশ অন ডেলিভারি ${taka(s.cod_limit, 'bn')} পর্যন্ত অর্ডারে প্রযোজ্য।` })}</p>}
          {pay !== 'cod' && (
            <div className="mt-3 space-y-3 rounded-md border border-[#E5E7EB] bg-[#F9FAFB] p-4 text-sm" data-testid="mobile-pay-box">
              <p>{t({ en: `Send ${taka(total, 'en')} (Send Money / Payment) to`, bn: `${taka(total, 'bn')} পাঠান (সেন্ড মানি / পেমেন্ট)`})} <b data-testid="pay-number">{pay === 'bkash' ? s?.bkash_number : s?.nagad_number}</b> ({pay === 'bkash' ? 'bKash' : 'Nagad'}), {t({ en: 'then enter the transaction ID.', bn: 'তারপর ট্রানজ্যাকশন আইডি দিন।' })}</p>
              <input required className={field} placeholder={t({ en: 'Transaction ID (TrxID) *', bn: 'ট্রানজ্যাকশন আইডি *' })} value={txn} onChange={(e) => setTxn(e.target.value)} data-testid="co-txn" />
              <label className="inline-flex cursor-pointer items-center gap-2 text-[#0E9A49]" data-testid="co-screenshot-label">
                {shot ? <CheckCircle2 className="h-4 w-4" /> : <Upload className="h-4 w-4" />} {shot ? t({ en: 'Screenshot attached', bn: 'স্ক্রিনশট যুক্ত হয়েছে' }) : t({ en: 'Attach payment screenshot (optional)', bn: 'পেমেন্টের স্ক্রিনশট দিন (ঐচ্ছিক)' })}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="co-screenshot" />
              </label>
            </div>
          )}
        </section>
      </div>

      <aside className="h-fit space-y-4 rounded-xl border border-[#E5E7EB] p-5 lg:sticky lg:top-24" data-testid="order-summary">
        <h2 className="shop-heading text-xl">{t({ en: 'Order summary', bn: 'অর্ডার সারাংশ' })}</h2>
        <CartLines compact />
        <div className="flex gap-2">
          <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder={t({ en: 'Coupon code', bn: 'কুপন কোড' })} className={field} data-testid="co-coupon" />
          <button type="button" onClick={applyCoupon} disabled={!coupon} className="shop-btn border border-[#E5E7EB]" data-testid="co-apply-coupon">{t({ en: 'Apply', bn: 'প্রয়োগ' })}</button>
        </div>
        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between"><dt>{t({ en: 'Subtotal', bn: 'সাবটোটাল' })}</dt><dd>{taka(cart.subtotal, language)}</dd></div>
          {disc && <div className="flex justify-between text-[#0E9A49]" data-testid="co-discount"><dt>{t({ en: 'Discount', bn: 'ছাড়' })} ({disc.code})</dt><dd>-{taka(disc.discount, language)}</dd></div>}
          <div className="flex justify-between"><dt>{t({ en: 'Delivery', bn: 'ডেলিভারি' })} {zone && <span className="text-[#6B7280]">({t(zone.name)}, {zone.days} {t({ en: 'days', bn: 'দিন' })})</span>}</dt><dd data-testid="co-delivery">{zone ? (delivery ? taka(delivery, language) : t({ en: 'Free', bn: 'ফ্রি' })) : '—'}</dd></div>
          <div className="flex justify-between border-t border-[#E5E7EB] pt-2 text-base font-semibold"><dt>{t({ en: 'Total', bn: 'মোট' })}</dt><dd data-testid="co-total">{taka(total, language)}</dd></div>
        </dl>
        {err && <p className="rounded-md bg-[#FEE2E2] p-2 text-sm text-[#E31E24]" role="alert" data-testid="co-error">{err}</p>}
        <button type="submit" disabled={busy} className="shop-btn flex w-full items-center justify-center gap-2 bg-[#E31E24] py-3 text-base text-white hover:bg-[#c8181d] disabled:opacity-60" data-testid="co-place-order">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t({ en: 'Place order', bn: 'অর্ডার করুন' })}
        </button>
        <PayLogos />
      </aside>
    </form>
  );
}
